import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { initialsOf } from '../core/domain/person';

/**
 * Badge d'initiales ouvrant le menu du compte.
 *
 * Remplace l'affichage de l'adresse e-mail dans l'en-tête : sur mobile, l'e-mail
 * était déjà masqué, et sur grand écran il n'apporte rien une fois connecté.
 * Le menu regroupe ce qui concerne la session — profil, préférences,
 * déconnexion — sans élargir la barre.
 *
 * Le composant ne connaît ni `AuthService` ni la navigation de sortie : il
 * émet `signedOut` et laisse la coquille orchestrer. Il reste ainsi un atome
 * du système de design, réutilisable hors de l'en-tête.
 */
@Component({
  selector: 'ct-account-menu',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
  template: `
    <div class="account">
      <button
        type="button"
        class="badge"
        [attr.aria-expanded]="open()"
        aria-haspopup="menu"
        [attr.aria-controls]="menuId"
        [attr.aria-label]="label()"
        (click)="toggle()"
      >
        <span aria-hidden="true">{{ initials() }}</span>
      </button>

      @if (open()) {
        <div class="menu" role="menu" [id]="menuId" [attr.aria-label]="label()">
          <p class="who">
            <span class="name">{{ displayName() }}</span>
            @if (fullName(); as name) {
              <span class="email">{{ email() }}</span>
            }
          </p>

          <a role="menuitem" routerLink="/profil" (click)="close()">Mon profil</a>
          <a role="menuitem" routerLink="/mes-retraits" (click)="close()">Mes retraits</a>
          <a role="menuitem" routerLink="/mes-commandes" (click)="close()">Mes commandes</a>
          <a role="menuitem" routerLink="/preferences" (click)="close()">Préférences</a>

          <button type="button" role="menuitem" class="danger" (click)="signOut()">
            Se déconnecter
          </button>
        </div>
      }
    </div>
  `,
  styles: `
    .account {
      position: relative;
    }

    .badge {
      display: grid;
      place-items: center;
      width: 2.25rem;
      height: 2.25rem;
      padding: 0;
      border: 1px solid var(--ct-vert-cageot);
      border-radius: 50%;
      background: var(--ct-vert-cageot);
      color: var(--ct-kraft);
      font-family: var(--ct-font-title);
      font-size: var(--ct-size-sm);
      font-weight: var(--ct-weight-bold);
      letter-spacing: 0.02em;
      cursor: pointer;
      transition: filter var(--ct-transition);

      &:hover {
        filter: brightness(1.08);
      }

      &:focus-visible {
        outline: 2px solid var(--ct-focus);
        outline-offset: 2px;
      }

      &[aria-expanded='true'] {
        background: var(--ct-vert-cageot-light);
        border-color: var(--ct-vert-cageot-light);
      }
    }

    .menu {
      position: absolute;
      top: calc(100% + var(--ct-space-2));
      right: 0;
      z-index: 6;
      min-width: 14rem;
      padding: var(--ct-space-2);
      background: var(--ct-white);
      border: 1px solid var(--ct-border);
      border-radius: var(--ct-radius-md);
      box-shadow: 0 0.5rem 1.25rem rgb(61 46 34 / 12%);
      display: flex;
      flex-direction: column;
      gap: var(--ct-space-1);
    }

    .who {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      padding: var(--ct-space-2) var(--ct-space-3);
      margin: 0 0 var(--ct-space-1);
      border-bottom: 1px solid var(--ct-border);
    }

    .name {
      font-size: var(--ct-size-md);
      font-weight: var(--ct-weight-bold);
      color: var(--ct-text);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .email {
      font-size: var(--ct-size-sm);
      color: var(--ct-text-muted);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    a[role='menuitem'],
    button[role='menuitem'] {
      display: block;
      width: 100%;
      padding: var(--ct-space-2) var(--ct-space-3);
      border: 0;
      border-radius: var(--ct-radius-sm);
      background: transparent;
      color: var(--ct-text);
      font: inherit;
      font-size: var(--ct-size-md);
      text-align: left;
      text-decoration: none;
      cursor: pointer;

      &:hover,
      &:focus-visible {
        background: var(--ct-kraft);
        outline: none;
      }
    }

    .danger {
      color: var(--ct-rouge-tomate);
    }
  `,
})
export class CtAccountMenu {
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly email = input.required<string>();
  readonly fullName = input<string | undefined>();
  readonly signedOut = output<void>();

  protected readonly open = signal(false);
  protected readonly menuId = 'ct-account-menu';

  protected readonly displayName = computed(() => this.fullName()?.trim() || this.email());

  protected readonly initials = computed(() => initialsOf(this.fullName()?.trim() || this.email()));

  protected readonly label = computed(() => `Compte de ${this.displayName()}`);

  protected toggle(): void {
    this.open.update((value) => !value);
  }

  protected close(): void {
    this.open.set(false);
  }

  protected signOut(): void {
    this.close();
    this.signedOut.emit();
  }

  @HostListener('document:pointerdown', ['$event'])
  protected onDocumentPointerDown(event: PointerEvent): void {
    if (!this.open()) {
      return;
    }
    const target = event.target;
    if (target instanceof Node && !this.host.nativeElement.contains(target)) {
      this.close();
    }
  }

  @HostListener('document:keydown', ['$event'])
  protected onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.open()) {
      this.close();
    }
  }
}
